import React, { useCallback } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BypassLayout } from '../components/common';
import { useAuth } from '../context/AuthContext';
import StudentRosterPanel from '../components/StudentAdminV2/StudentRosterPanel';
import StatsPanel from '../components/StudentAdminV2/StatsPanel';
import TeacherStatsPanel from '../components/StudentAdminV2/TeacherStatsPanel';
import StudentDetailPanel from '../components/StudentAdminV2/StudentDetailPanel';
import ClassAdminPanel from '../components/StudentAdminV2/ClassAdminPanel';
import ActivityLogsPage from '../components/StudentAdminV2/ActivityLogsPage';
import { listStaff } from '../components/StudentAdminV2/academyApi';
import { useAsync, useElementWidth } from '../components/StudentAdminV2/ui';
import ReportPage, { REPORT_TITLES } from '../components/StudentAdminV2/stats/ReportPage';
import { dashboardSearch } from '../components/StudentAdminV2/stats/reportModel';

/**
 * 「학생」 v2 페이지 (/academy) — 학생관리 v1 과 별개로 만들고 나중에 합친다(오너 2026-10-10).
 *
 * 구조: 왼쪽 = 학생 표(학습 v2 의 좁은 표) / 오른쪽 = 학생을 고르지 않으면 **통계 페이지**, 고르면 학생 상세.
 * 학습 v2 와 달리 표가 화면 전체를 쓰는 상태가 없다 — 그 자리를 통계 페이지가 채운다(설계 §1).
 * 본문이 960px 이상이면 목록과 현황을 나란히 표시한다(왼쪽 clamp(480px, 40%, 560px) · 간격 16).
 * 그보다 좁으면 목록과 현황을 번갈아 표시한다. 창 폭 대신 사이드바를 뺀 본문 폭을 기준으로 한다.
 *
 * 주소가 정본이다(설계 §1-2) — 통계 탭 ?stats= · 상세 탭 ?tab=. 학생을 고르거나 닫을 때 두 값을 함께 옮겨,
 * 닫으면 마지막 통계 탭으로 돌아온다. 탭을 기억하는 상태값은 두지 않는다(문제 v2 상세의 관례).
 * 휴대폰은 칸이 하나라 ?stats= 가 있으면 통계, 없으면 목록이다(설계 §7).
 *
 * 통계는 계정 유형마다 다른 화면이다(오너 지시) — 매니저는 학원 전체(StatsPanel), 강사는 본인 반(TeacherStatsPanel).
 * 통계 맨 아래 [반 관리]/[내 반 정보]를 누르면 오른쪽 칸이 반 화면(ClassAdminPanel)으로 바뀐다 — ?stats=classes(&class=번호).
 * 관리자 배지 더블클릭 → /academy/activity. 우측 전용 활동 페이지에서 진입 번호를 확인하고 기록을 조회한다.
 *
 * 설계: docs/student-admin-v2/1010_SPEC_student-admin-v2-screen.html · 요청서 FE-20261009-student-admin-v2-backend-design.html
 */
function StudentAdmin() {
  const [layoutRef, layoutWidth] = useElementWidth();
  const { studentId, report } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userType: authUserType, getAccessToken } = useAuth();
  const isTeacher = authUserType === 'teacher';
  const isManager = authUserType === 'manager';
  const isActivity = location.pathname === '/academy/activity';
  const defaultStats = isTeacher ? 'my' : 'overview';
  const statsParam = params.get('stats');
  const statsTab = statsParam || defaultStats;
  const detailTab = params.get('tab') || 'overview';

  // 설정(A4 · /settings)은 부르지 않는다 — 일괄 이관이 없어 「이관 전」 띠와 이관일이 틀린 안내가 된다(백엔드 4단계 전달 §1 · §4-A)
  // 강사의 직원 번호 — 직원 목록(E1)은 강사에게 본인 것만 준다(요청서 §10). 매니저는 부르지 않는다
  const me = useAsync((signal) => (isTeacher ? listStaff({ signal }, getAccessToken).then((rows) => rows[0] || null) : Promise.resolve(null)), `me-${isTeacher}`);
  const myStaffId = me.data?.staff_id ?? null;

  const carry = useCallback((extra = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(extra).forEach(([k, v]) => (v == null ? next.delete(k) : next.set(k, v)));
    const s = next.toString();
    return s ? `?${s}` : '';
  }, [params]);

  const closeDetail = useCallback(() => {
    const returnReport = params.get('return_report');
    const prefix = isManager && Object.hasOwn(REPORT_TITLES, returnReport) ? `/academy/stats/${returnReport}` : '/academy';
    navigate(`${prefix}${carry({ tab: null, return_report: null })}`);
  }, [navigate, carry, params, isManager]);
  // 이미 고른 학생을 다시 누르면 선택을 풀고 통계 페이지로 — 「닫기」와 같다(오너 2026-10-10)
  const openStudent = useCallback((id, targetTab) => {
    if (studentId && String(id) === String(studentId)) { closeDetail(); return; }
    navigate(`/academy/student/${id}${carry({ ...(targetTab ? { tab: targetTab } : {}), ...(report ? { return_report: report } : {}) })}`);
  }, [navigate, carry, studentId, closeDetail, report]);
  const changeStatsTab = useCallback((k) => setParams(new URLSearchParams(carry({ stats: k }).slice(1)), { replace: true }), [setParams, carry]);
  const changeDetailTab = useCallback((k) => setParams(new URLSearchParams(carry({ tab: k }).slice(1)), { replace: true }), [setParams, carry]);
  const changeOverviewFilters = useCallback((changes) => setParams(new URLSearchParams(carry(changes).slice(1)), { replace: true }), [setParams, carry]);
  const openReport = useCallback((kind, values = {}) => {
    const next = new URLSearchParams(dashboardSearch(params));
    next.set('home', dashboardSearch(params));
    Object.entries(values).forEach(([key, value]) => { if (value != null && value !== '') next.set(key, value); });
    navigate(`/academy/stats/${kind}?${next}`);
  }, [navigate, params]);
  const backFromReport = useCallback(() => navigate(`/academy${dashboardSearch(params.get('home') || params)}`), [navigate, params]);
  const openClasses = useCallback(() => navigate('/academy?stats=classes'), [navigate]);
  const selectClass = useCallback((id) => navigate(`/academy?stats=classes${id ? `&class=${id}` : ''}`), [navigate]);
  const changeSettingsTab = useCallback((k) => navigate(`/academy?stats=classes${k && k !== 'classes' ? `&set=${k}` : ''}`, { replace: true }), [navigate]);
  const backFromClasses = useCallback(() => navigate(`/academy?stats=${defaultStats}`), [navigate, defaultStats]);
  const openStatsMobile = useCallback(() => navigate(`/academy?stats=${defaultStats}`), [navigate, defaultStats]);
  const closeStatsMobile = useCallback(() => navigate('/academy'), [navigate]);
  const closeActivityLogs = useCallback(() => {
    const previous = location.state?.activityReturnTo;
    navigate(previous && /^\/academy(?:\?|\/student\/\d+(?:\?|$)|$)/.test(previous) ? previous : '/academy?stats=overview');
  }, [navigate, location.state]);

  let stats;
  if (isActivity && isManager) {
    stats = <ActivityLogsPage key={location.key} onBack={closeActivityLogs} />;
  } else if (report && isManager && Object.hasOwn(REPORT_TITLES, report)) {
    stats = <ReportPage report={report} filters={Object.fromEntries(params)} onChange={changeOverviewFilters} onBack={backFromReport} onOpenStudent={openStudent} scrollKey={`${location.pathname}${location.search}`} />;
  } else if (statsTab === 'classes') {
    stats = (
      <ClassAdminPanel
        isTeacher={isTeacher}
        myStaffId={myStaffId}
        classId={params.get('class') ? Number(params.get('class')) : null}
        settingsTab={params.get('set') || 'classes'}
        onSettingsTab={changeSettingsTab}
        onSelectClass={selectClass}
        onBack={backFromClasses}
        onOpenStudent={openStudent}
      />
    );
  } else if (isTeacher) {
    stats = <TeacherStatsPanel me={me.data} meLoading={me.loading} meError={me.error} onRetryMe={me.reload} tab={statsTab} onTabChange={changeStatsTab} onOpenStudent={openStudent} onOpenClasses={openClasses} />;
  } else {
    stats = <StatsPanel onOpenClasses={openClasses} filters={Object.fromEntries(params)} onFiltersChange={changeOverviewFilters} onOpenStudent={openStudent} onOpenReport={openReport} onOpenNotes={(classId) => openReport('notes', { note_class: classId ?? 'academy' })} scrollKey={`overview${dashboardSearch(params)}`} />;
  }
  const detail = studentId ? (
    <StudentDetailPanel
      key={studentId}
      studentId={studentId}
      isTeacher={isTeacher}
      myStaffId={myStaffId}
      tab={detailTab}
      onTabChange={changeDetailTab}
      onClose={closeDetail}
    />
  ) : null;
  if (isActivity && !isManager) return <Navigate to="/academy" replace />;
  if (report && (!isManager || !Object.hasOwn(REPORT_TITLES, report))) return <Navigate to={`/academy?stats=${defaultStats}`} replace />;
  return (
    <BypassLayout
      renderContent={({ isMobileView }) => {
        if (isMobileView || (layoutWidth > 0 && layoutWidth < 960)) {
          return (
            <div ref={layoutRef} className={`flex flex-col gap-2 ${isMobileView ? 'flex-1' : 'h-[calc(100vh-6rem)] w-full'}`} style={isMobileView ? { width: 'calc(100% - 28.8px)', height: 'calc(100% - 28.8px)', margin: '14.4px' } : undefined}>
              <div className="flex min-h-0 flex-1 flex-col">
                {detail || (statsParam || isActivity || report
                  ? (isActivity || report ? stats : React.cloneElement(stats, { onBack: closeStatsMobile }))
                  : <StudentRosterPanel selectedStudentId={null} onSelectStudent={openStudent} isTeacher={isTeacher} myStaffId={myStaffId} onOpenStats={openStatsMobile} />)}
              </div>
            </div>
          );
        }
        return (
          <div ref={layoutRef} className="flex h-[calc(100vh-6rem)] w-full flex-col gap-2">
            <div className="flex min-h-0 w-full flex-1 gap-4">
              <div className="h-full shrink-0" style={{ width: 'clamp(480px, 40%, 560px)' }}>
                <StudentRosterPanel
                  selectedStudentId={studentId ? parseInt(studentId, 10) : null}
                  onSelectStudent={openStudent}
                  isTeacher={isTeacher}
                  myStaffId={myStaffId}
                />
              </div>
              <div className="h-full min-w-0 flex-1">
                {detail || stats}
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}

export default StudentAdmin;
